import { atom } from '@reatom/framework'
import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import styled from 'styled-components'

import { AsyncStateN, Result } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import {
  startWalletDepositFlowEvent,
  startWalletTransferFlowEvent,
  walletsAtom,
} from '@root/entities/wallet/atoms'
import {
  reatomWalletDegenHours,
  reatomWalletsTradingAnalytics,
} from '@root/features/services/wallet-profile'
import { userTradingAnalyticsPeriodAtom } from '@root/features/user/user-trading-analytics/atoms'
import {
  USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP,
  USER_TRADING_ANALYTICS_PERIODS,
} from '@root/features/user/user-trading-analytics/models'
import { useTranslation } from '@root/shared/localization'

import { mergeHoldings } from '../../model/portfolio-readings'
import {
  pickPortfolioWalletAction,
  portfolioViewAtom,
  portfolioWalletAtom,
  setPortfolioListAction,
  setPortfolioTabAction,
  togglePortfolioUnitAction,
} from '../../model/portfolio-view-atom'
import { BoardNav } from '../pulse-screen/board-nav'
import { pulseV2Styles } from '../pulse-screen/pulse-v2.styles'

import { shortAddress, usd } from './money'
import { reatomPortfolioHistory } from './portfolio-history'
import {
  HistoryList,
  HoldingsList,
  PositionsList,
  WalletsList,
} from './portfolio-lists'
import {
  AnalysisPanel,
  BalancePanel,
  GuestBalancePanel,
  CurvePanel,
  EntryPanel,
  HoursPanel,
  PerformancePanel,
} from './portfolio-panels'
import { portfolioStyles } from './portfolio.styles'
import {
  createServerWalletAction,
  exportServerWalletAction,
  importServerWalletsAction,
  walletCommandText,
} from './wallet-commands'
import {
  CreateWalletDialog,
  ExportWalletDialog,
  ImportWalletsDialog,
} from './wallet-dialogs'

import type { HoldingPart } from '../../model/portfolio-readings'
import type {
  PortfolioList,
  PortfolioTab,
} from '../../model/portfolio-view-atom'
import type { Address } from '@root/entities/address/Address'
import type { WalletId } from '@root/entities/wallet'

/**
 * The account screen: what the money is, what it did, and where it sits.
 *
 * Built from two sides. The layout and a few of the readings are Axiom's, which
 * is the shape of this screen everywhere in this market and there is no point
 * being different about it: balance, a realised curve, the five return bands,
 * positions on one side and the tape on the other. The rest is ours and is why
 * the page is not a copy — the entry market caps, the funding line, and the
 * week of hours coloured by what a trade in that hour actually made.
 *
 * Every figure comes from a read that answers. Nothing here is fed by the
 * `user.*` endpoints the old portfolio page was built against, which is why
 * that page is still off the router.
 */
export const PortfolioScreen: React.FC = memo(() => {
  const [view] = useAtom(portfolioViewAtom)
  const setTab = useAction(setPortfolioTabAction)
  const guest = useGuestAddress()

  return (
    <Screen>
      <div className="app">
        <div className="glow" />

        <BoardNav active="portfolio" />

        <div className="bar">
          <h1>Portfolio</h1>
          {guest ? (
            <span className="tag" title={guest}>
              {shortAddress(guest, 5)}
            </span>
          ) : (
            <div className="seg">
              <button
                className={view.tab === 'portfolio' ? 'on' : undefined}
                type="button"
                onClick={() => setTab('portfolio')}
              >
                Overview
              </button>
              <button
                className={view.tab === 'wallets' ? 'on' : undefined}
                type="button"
                onClick={() => setTab('wallets')}
              >
                Wallets
              </button>
            </div>
          )}

          <div className="barright">
            {guest ? (
              <a className="btn" href="/portfolio-v2">
                Back to mine
              </a>
            ) : (
              <WalletPicker />
            )}
            <UnitToggle />
          </div>
        </div>

        <div className="body">
          {guest ? <Overview guest={guest} /> : <Body tab={view.tab} />}
        </div>
      </div>
    </Screen>
  )
})

/**
 * Nothing to read until there is a wallet.
 *
 * Six panels of dashes is not an empty state, it is a page that looks broken.
 * An account with no wallet has traded nothing — which is a true answer and
 * fits in one line, with the one thing to do next beside it.
 */
const Body: React.FC<{ tab: PortfolioTab }> = memo(({ tab }) => {
  const [wallets] = useAtom(walletsAtom)
  const setTab = useAction(setPortfolioTabAction)

  if (wallets.length === 0) {
    return (
      <>
        <section className="pn" style={{ marginTop: 10 }}>
          <header>
            <h2>No wallets yet</h2>
          </header>
          <p className="blank">
            Everything on this page is read from your wallets, and there are
            none on this account. Make one below — it takes one press, and the
            balances, the positions and the week of hours fill in from the first
            trade.
            <br />
            <br />
            {tab === 'portfolio' ? (
              <button
                className="btn"
                type="button"
                onClick={() => setTab('wallets')}
              >
                Create or import a wallet
              </button>
            ) : undefined}
          </p>
        </section>
        {/*
          The wallets tab is shown even with nothing in it, because it is where
          the two buttons live. Sending somebody to the old wallet manager
          instead is what this screen used to do, and that screen cannot make a
          wallet in a browser at all.
        */}
        {tab === 'wallets' ? <Wallets /> : undefined}
      </>
    )
  }

  return tab === 'portfolio' ? <Overview /> : <Wallets />
})

/**
 * Somebody else's wallet, when the address is in the url.
 *
 * The same screen, read-only: the reads behind every panel take an address and
 * do not care whose it is. It is how this page gets looked at with a week of
 * real trading in it rather than an empty account, and on its own terms it is
 * what a product about following traders is for — their portfolio is the thing
 * worth seeing.
 *
 * Only the panels that are about trading. Balance and the wallet list are
 * about wallets we hold, and there is nothing honest to put in them here.
 */
const useGuestAddress = (): string | undefined => {
  const [params] = useSearchParams()
  const address = params.get('wallet')?.trim()

  return address && address.length >= 32 && address.length <= 64
    ? address
    : undefined
}

/** Which wallets every figure on the page is about. */
const WalletPicker: React.FC = memo(() => {
  const [wallets] = useAtom(walletsAtom)
  const [picked] = useAtom(portfolioWalletAtom)
  const pick = useAction(pickPortfolioWalletAction)

  /* A wallet's name is an atom of its own, so the options are built where
     those atoms can be read rather than by touching `.name` as a string. */
  const [options] = useAtom(
    (ctx) =>
      wallets.map((wallet) => ({
        id: wallet.id,
        label: `${ctx.spy(wallet.name)} · ${shortAddress(String(wallet.address))}`,
      })),
    [wallets],
  )

  return (
    <select
      className="sel"
      aria-label="Which wallets"
      value={picked ?? 'all'}
      onChange={(event) =>
        pick(
          event.target.value === 'all'
            ? undefined
            : (event.target.value as WalletId),
        )
      }
    >
      <option value="all">All wallets ({wallets.length})</option>
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.label}
        </option>
      ))}
    </select>
  )
})

const UnitToggle: React.FC = memo(() => {
  const [view] = useAtom(portfolioViewAtom)
  const toggle = useAction(togglePortfolioUnitAction)

  return (
    <button
      className="btn"
      type="button"
      onClick={() => toggle()}
      title="Show figures in dollars or in SOL"
    >
      <svg className="io" viewBox="0 0 24 24">
        <path d="M7 10h11l-3-3M17 14H6l3 3" />
      </svg>
      {view.unit === 'usd' ? 'USD' : 'SOL'}
    </button>
  )
})

/** Everything about the money, on one scroll. */
const Overview: React.FC<{ guest?: string }> = memo(({ guest }) => {
  const { t } = useTranslation()
  const [view] = useAtom(portfolioViewAtom)
  const setList = useAction(setPortfolioListAction)
  const [network] = useAtom(selectedNetworkAtom)
  const [period] = useAtom(userTradingAnalyticsPeriodAtom)
  const setPeriod = useAction(userTradingAnalyticsPeriodAtom)

  const [wallets] = useAtom(walletsAtom)
  const [picked] = useAtom(portfolioWalletAtom)

  /* Somebody else's wallet is not one of ours, so none of ours are chosen. */
  const chosen = useMemo(() => {
    if (guest !== undefined) {
      return []
    }
    return picked === undefined
      ? wallets
      : wallets.filter((wallet) => wallet.id === picked)
  }, [wallets, picked, guest])

  /*
   * Which addresses to ask about, as one string.
   *
   * A wallet model's identity changes whenever its balance ticks, so a list
   * built from the models is a new list every few seconds — and anything keyed
   * on it would refetch the whole page that often. The addresses themselves do
   * not change, so the joined string is the dependency, and the list is read
   * back out of it. That keeps what the atom closes over and what rebuilds it
   * the same thing.
   */
  const addressKey = guest ?? chosen.map((wallet) => wallet.address).join(',')

  const addressesAtom = useMemo(() => {
    const addresses: ReadonlyArray<Address> = (
      addressKey === '' ? [] : addressKey.split(',')
    ).map((address) => address as Address)

    // eslint-disable-next-line @reatom/unit-naming-rule
    return atom(() => addresses, 'portfolioAddressesAtom')
  }, [addressKey])

  const analytics = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomWalletsTradingAnalytics({ walletsAtom: addressesAtom, network }),
    [addressesAtom, network],
  )
  const history = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomPortfolioHistory({ addressesAtom, network }),
    [addressesAtom, network],
  )

  const [profileState] = useAtom(analytics.profile)
  const [historyState] = useAtom(history)

  const profile = AsyncStateN.matchWaiting(profileState, {
    Waiting: () => undefined,
    Error: () => undefined,
    Success: (value) => value,
  })
  const trades = AsyncStateN.matchWaiting(historyState, {
    Waiting: () => undefined,
    Error: () => undefined,
    Success: (value) => value,
  })

  /*
   * Everything the wallets themselves say, in one read.
   *
   * Balances, the spendable coin and the merged holdings all come from the
   * same per-wallet asset atoms, so they are summed together — a total that
   * disagreed with the list under it would be the page arguing with itself.
   * The app's own totals atom is not used: it is always about every wallet,
   * and this page can be about one.
   */
  const [book] = useAtom(
    (ctx) => {
      const parts = chosen.flatMap((wallet) =>
        ctx.spy(wallet.visibleAssets).map(
          (asset): HoldingPart => ({
            address: String(asset.currencyAddress),
            ticker: asset.currency.ticker,
            title: asset.currency.title,
            icon: asset.currency.icon.toString(),
            amount: ctx.spy(asset.balance).toNumber(),
            valueUsd: ctx.spy(asset.balanceUsd).toNumber(),
            priceUsd: ctx.spy(asset.priceUsd).toNumber(),
          }),
        ),
      )

      return {
        holdings: mergeHoldings(parts),
        totalUsd: chosen.reduce(
          (sum, wallet) => sum + ctx.spy(wallet.totalBalanceUsd).toNumber(),
          0,
        ),
        totalNative: chosen.reduce(
          (sum, wallet) => sum + ctx.spy(wallet.totalBalanceNative).toNumber(),
          0,
        ),
        spendableNative: chosen.reduce(
          (sum, wallet) => sum + ctx.spy(wallet.nativeAssetBalance).toNumber(),
          0,
        ),
        /* The heatmap is per wallet on the server, so when several are chosen
           it shows the one with the most in it and names which. Picking one
           silently would be a week of somebody else's hours. */
        richest: [...chosen].sort(
          (left, right) =>
            ctx.spy(right.totalBalanceUsd).toNumber() -
            ctx.spy(left.totalBalanceUsd).toNumber(),
        )[0],
      }
    },
    [chosen],
  )

  /*
   * Whose week of hours to read, as an address rather than a wallet.
   *
   * The model below is keyed on this, and a wallet model is a new object every
   * time a balance ticks — keying on one asked the server for the same week
   * over and over. An address is a string and does not move.
   */
  const hoursAddress = guest ?? (book.richest?.address as string | undefined)

  const hoursModel = useMemo(
    () =>
      hoursAddress === undefined
        ? undefined
        : // eslint-disable-next-line @reatom/unit-naming-rule
          reatomWalletDegenHours({
            walletAddress: hoursAddress as unknown as Address,
            network,
          }),
    [hoursAddress, network],
  )
  const [hoursState] = useAtom(
    (ctx) => (hoursModel ? ctx.spy(hoursModel) : undefined),
    [hoursModel],
  )
  const hours =
    hoursState === undefined
      ? undefined
      : AsyncStateN.matchWaiting(hoursState, {
          Waiting: () => undefined,
          Error: () => undefined,
          Success: (value) => value,
        })

  return (
    <div className="grid">
      {guest === undefined ? (
        <BalancePanel
          totalUsd={book.totalUsd}
          totalNative={book.totalNative}
          unrealised={profile?.totals.unrealizedPnl}
          spendableNative={book.spendableNative}
          tokens={book.holdings.length}
          wallets={chosen.length}
          unit={view.unit}
        />
      ) : (
        <GuestBalancePanel profile={profile} />
      )}

      <CurvePanel
        series={profile?.realizedPnlSeries ?? []}
        realised={profile?.totals.realizedPnl}
        unrealised={profile?.totals.unrealizedPnl}
        total={profile?.totals.totalPnl}
      >
        <div className="seg">
          {USER_TRADING_ANALYTICS_PERIODS.map((option) => (
            <button
              key={option}
              className={option === period ? 'on' : undefined}
              type="button"
              onClick={() => setPeriod(option)}
            >
              {t(USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP[option])}
            </button>
          ))}
        </div>
      </CurvePanel>

      <PerformancePanel
        totals={profile?.totals}
        distribution={profile?.distribution}
      />

      <AnalysisPanel totals={profile?.totals} positions={profile?.positions} />

      <EntryPanel caps={profile?.entryMarketCaps} funding={profile?.funding} />

      <HoursPanel
        hours={hours}
        walletName={
          guest === undefined && chosen.length <= 1
            ? undefined
            : shortAddress(hoursAddress ?? '')
        }
        loading={hoursModel !== undefined && hours === undefined}
      />

      <section className="pn lists">
        <header>
          <div className="seg">
            {LISTS.map((option) => (
              <button
                key={option.key}
                className={option.key === view.list ? 'on' : undefined}
                type="button"
                onClick={() => setList(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="right">
            <span className="tag">
              {countedFor(view.list, {
                traded: profile?.positions.length ?? 0,
                held: book.totalUsd,
                trades: trades?.length ?? 0,
              })}
            </span>
          </div>
        </header>

        {view.list === 'positions' ? (
          <PositionsList
            positions={profile?.positions ?? []}
            logoHost={(logo) => logo || undefined}
          />
        ) : undefined}
        {view.list === 'holdings' ? (
          <HoldingsList holdings={book.holdings} />
        ) : undefined}
        {view.list === 'history' ? (
          <HistoryList trades={trades ?? []} loading={trades === undefined} />
        ) : undefined}
      </section>
    </div>
  )
})

/** What the count beside the list tabs is counting. */
const countedFor = (
  list: PortfolioList,
  of: { traded: number; held: number; trades: number },
): string => {
  if (list === 'positions') {
    return `${of.traded} traded`
  }
  if (list === 'holdings') {
    return `${usd(of.held)} held`
  }
  return `${of.trades} trades`
}

const LISTS: ReadonlyArray<{ key: PortfolioList; label: string }> = [
  { key: 'positions', label: 'Positions' },
  { key: 'holdings', label: 'Holdings' },
  { key: 'history', label: 'History' },
]

/** The wallets, as things to act on. */
const Wallets: React.FC = memo(() => {
  const [wallets] = useAtom(walletsAtom)
  const deposit = useAction(startWalletDepositFlowEvent)
  const transfer = useAction(startWalletTransferFlowEvent)

  const create = useAction(createServerWalletAction)
  const importKeys = useAction(importServerWalletsAction)
  const reveal = useAction(exportServerWalletAction)

  const [dialog, setDialog] = useState<'create' | 'import' | undefined>()
  const [exporting, setExporting] = useState<
    { id: WalletId; name: string } | undefined
  >()
  const [secret, setSecret] = useState<string | undefined>()
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | undefined>()

  const [rows] = useAtom(
    (ctx) => {
      const built = wallets.map((wallet) => ({
        id: String(wallet.id),
        name: ctx.spy(wallet.name),
        address: String(wallet.address),
        colour: ctx.spy(wallet.color),
        usd: ctx.spy(wallet.totalBalanceUsd).toNumber(),
        native: Number(ctx.spy(wallet.totalBalanceNative).toFixed(3)),
        assets: ctx.spy(wallet.visibleAssets).length,
        // Only a wallet whose key we hold has one to hand back.
        exportable: ctx.spy(wallet.custody) === 'server',
      }))
      const whole = built.reduce((sum, row) => sum + row.usd, 0)

      return built
        .map((row) => ({
          ...row,
          share: whole <= 0 ? 0 : (row.usd / whole) * 100,
        }))
        .sort((left, right) => right.usd - left.usd)
    },
    [wallets],
  )

  const known = useMemo(
    () => new Set(wallets.map((wallet) => String(wallet.address))),
    [wallets],
  )

  const close = () => {
    setDialog(undefined)
    setExporting(undefined)
    setSecret(undefined)
    setProblem(undefined)
    setBusy(false)
  }

  return (
    <>
      <div className="wtop">
        <h2>Wallets</h2>
        <span className="tag">{rows.length}</span>
        <div className="right">
          <button
            className="btn"
            type="button"
            onClick={() => setDialog('import')}
          >
            Import
          </button>
          <button
            className="btn hot"
            type="button"
            onClick={() => setDialog('create')}
          >
            Create wallet
          </button>
        </div>
      </div>

      <WalletsList
        wallets={rows}
        onCopy={(address) =>
          navigator.clipboard.writeText(address).catch(() => undefined)
        }
        onDeposit={(id) => {
          const wallet = wallets.find(
            (candidate) => String(candidate.id) === id,
          )
          if (wallet) {
            deposit({ wallet })
          }
        }}
        onTransfer={(id) => {
          const wallet = wallets.find(
            (candidate) => String(candidate.id) === id,
          )
          if (wallet) {
            transfer({ wallet })
          }
        }}
        onExport={(id, name) => {
          setExporting({ id: id as WalletId, name })
          setSecret(undefined)
          setProblem(undefined)
        }}
        onManage={() => setDialog('create')}
      />

      {dialog === 'create' ? (
        <CreateWalletDialog
          onClose={close}
          busy={busy}
          error={problem}
          suggestedName={`Wallet ${rows.length + 1}`}
          onCreate={(input) => {
            setBusy(true)
            setProblem(undefined)
            void create({ name: input.name, color: input.colour }).then(
              (result) => {
                setBusy(false)
                if (Result.isSuccess(result)) {
                  close()
                } else {
                  setProblem(walletCommandText[result.failure])
                }
              },
            )
          }}
        />
      ) : undefined}

      {dialog === 'import' ? (
        <ImportWalletsDialog
          onClose={close}
          busy={busy}
          error={problem}
          known={known}
          onImport={(privateKeys) => {
            setBusy(true)
            setProblem(undefined)
            void importKeys(privateKeys).then((outcome) => {
              setBusy(false)
              if (outcome.failed.length === 0) {
                close()
                return
              }
              /* Some may have landed. Naming the first failure rather than a
                 count, because the reason is what the person acts on. */
              setProblem(
                `${outcome.done.length} imported. ${walletCommandText[outcome.failed[0] ?? 'unknown']}`,
              )
            })
          }}
        />
      ) : undefined}

      {exporting ? (
        <ExportWalletDialog
          onClose={close}
          walletName={exporting.name}
          secret={secret}
          busy={busy}
          error={problem}
          onReveal={() => {
            setBusy(true)
            setProblem(undefined)
            void reveal(exporting.id).then((result) => {
              setBusy(false)
              if (Result.isSuccess(result)) {
                setSecret(result.success.privateKey)
              } else {
                setProblem(walletCommandText[result.failure])
              }
            })
          }}
        />
      ) : undefined}
    </>
  )
})

const Screen = styled.div`
  position: fixed;
  inset: 0;

  ${pulseV2Styles}
  ${portfolioStyles}
`
